import React, { useEffect, useState } from 'react';
import { TransferProofService, TransferProof } from '../services/transfer-proof.service';
import { useNotification } from '../context/NotificationContext';
import { useTranslation } from 'react-i18next';
import styles from './MyTransferProofsPage.module.css';

export default function MyTransferProofsPage() {
  const [proofs, setProofs] = useState<TransferProof[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const [selectedProof, setSelectedProof] = useState<TransferProof | null>(null);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState<string | null>(null);
  const { addNotification } = useNotification();
  const { t } = useTranslation();
  const apiBase =
    import.meta.env.VITE_API_URL ||
    `${window.location.protocol}//${window.location.hostname}:3000/api`;
  const baseUrl = apiBase.replace('/api', '');
  const buildFileUrl = (fileUrl?: string | null) => (fileUrl ? `${baseUrl}${fileUrl}` : '');

  useEffect(() => {
    loadProofs();
  }, [filter]);

  useEffect(() => {
    if (!selectedProof?.fileUrl || selectedProof.mimeType !== 'application/pdf') {
      setPdfPreviewUrl(null);
      return;
    }

    let cancelled = false;
    let objectUrl: string | null = null;

    const loadPdf = async () => {
      try {
        const response = await fetch(buildFileUrl(selectedProof.fileUrl), { credentials: 'include' });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setPdfPreviewUrl(objectUrl);
        }
      } catch (error) {
        if (!cancelled) {
          setPdfPreviewUrl(null);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [selectedProof?.id, selectedProof?.fileUrl, selectedProof?.mimeType]);

  const loadProofs = async () => {
    setLoading(true);
    try {
      const res = await TransferProofService.getAll(
        filter ? { status: filter } : undefined
      );
      setProofs(res.data);
    } catch (error: any) {
      addNotification('Error cargando comprobantes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { text: string; className: string }> = {
      PENDING: { text: t('transfer_proofs.status_pending'), className: styles.statusPending },
      APPROVED: { text: t('transfer_proofs.status_approved'), className: styles.statusApproved },
      REJECTED: { text: t('transfer_proofs.status_rejected'), className: styles.statusRejected },
    };
    return badges[status] || { text: status, className: '' };
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>{t('transfer_proofs.page_title')}</h1>
        <p className={styles.subtitle}>
          {t('transfer_proofs.page_subtitle')}
        </p>
      </div>

      <div className={styles.filters}>
        <button
          className={filter === '' ? styles.filterActive : styles.filterButton}
          onClick={() => setFilter('')}
        >
          {t('transfer_proofs.filter_all')}
        </button>
        <button
          className={filter === 'PENDING' ? styles.filterActive : styles.filterButton}
          onClick={() => setFilter('PENDING')}
        >
          {t('transfer_proofs.filter_pending')}
        </button>
        <button
          className={filter === 'APPROVED' ? styles.filterActive : styles.filterButton}
          onClick={() => setFilter('APPROVED')}
        >
          {t('transfer_proofs.filter_approved')}
        </button>
        <button
          className={filter === 'REJECTED' ? styles.filterActive : styles.filterButton}
          onClick={() => setFilter('REJECTED')}
        >
          {t('transfer_proofs.filter_rejected')}
        </button>
      </div>

      {proofs.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>📋</div>
          <h2>{t('transfer_proofs.no_proofs')}</h2>
          <p>
            {filter
              ? `${t('common.status')}: "${getStatusBadge(filter).text}"`
              : t('transfer_proofs.no_proofs_hint')}
          </p>
        </div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('transfer_proofs.table_date')}</th>
                <th>{t('transfer_proofs.table_project')}</th>
                <th>{t('transfer_proofs.table_amount')}</th>
                <th>{t('transfer_proofs.table_status')}</th>
                <th>{t('transfer_proofs.file_label')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {proofs.map((proof) => {
                const badge = getStatusBadge(proof.status);
                return (
                  <tr key={proof.id}>
                    <td>{new Date(proof.createdAt).toLocaleDateString('es-MX')}</td>
                    <td>
                      {proof.development?.title || `ID: ${proof.developmentId}`}
                    </td>
                    <td className={styles.amount}>
                      ${Number(proof.amount).toLocaleString('es-MX')} {proof.currency}
                    </td>
                    <td>
                      <span className={`${styles.badge} ${badge.className}`}>
                        {badge.text}
                      </span>
                    </td>
                    <td>
                      <button
                        onClick={() => setSelectedProof(proof)}
                        className={styles.fileLink}
                      >
                        📄 {t('transfer_proofs.view_file')}
                      </button>
                    </td>
                    <td>
                      {proof.status === 'REJECTED' && proof.rejectionReason && (
                        <div className={styles.rejection}>
                          <strong>Motivo:</strong> {proof.rejectionReason}
                        </div>
                      )}
                      {proof.reviewedAt && (
                        <div className={styles.reviewDate}>
                          Revisado: {new Date(proof.reviewedAt).toLocaleDateString('es-MX')}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de vista previa de archivo */}
      {selectedProof && (
        <div className={styles.modalOverlay} onClick={() => setSelectedProof(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.closeBtn}
              onClick={() => setSelectedProof(null)}
            >
              ✕
            </button>

            <h2 className={styles.modalTitle}>{t('transfer_proofs.view_file')}</h2>

            <div className={styles.modalContent}>
              <div className={styles.proofInfo}>
                <p>
                  <strong>{t('transfer_proofs.table_project')}:</strong> {selectedProof.development?.title || `ID: ${selectedProof.developmentId}`}
                </p>
                <p>
                  <strong>{t('transfer_proofs.table_amount')}:</strong> ${Number(selectedProof.amount).toLocaleString('es-MX')} {selectedProof.currency}
                </p>
                <p>
                  <strong>{t('transfer_proofs.table_date')}:</strong> {new Date(selectedProof.createdAt).toLocaleDateString('es-MX')}
                </p>
                <p>
                  <strong>ID:</strong> #{selectedProof.id}
                </p>
              </div>

              <div className={styles.filePreview}>
                <h3>{t('transfer_proofs.file_label')}</h3>
                
                {selectedProof.fileUrl && (
                  <>
                    {selectedProof.mimeType === 'application/pdf' ? (
                      pdfPreviewUrl ? (
                        <iframe
                          src={pdfPreviewUrl}
                          className={styles.pdfViewer}
                          title="Comprobante PDF"
                        />
                      ) : (
                        <div className={styles.pdfViewer}>Cargando PDF...</div>
                      )
                    ) : (
                      <img
                        src={buildFileUrl(selectedProof.fileUrl)}
                        alt="Comprobante"
                        className={styles.imageViewer}
                        onError={(e) => {
                          console.error('Error loading image:', selectedProof.fileUrl);
                          (e.target as HTMLImageElement).style.display = 'none';
                          const errorDiv = document.createElement('div');
                          errorDiv.innerHTML = `<p style="color: red; text-align: center; padding: 20px;">Error al cargar la imagen</p>`;
                          (e.target as HTMLImageElement).parentNode?.appendChild(errorDiv);
                        }}
                      />
                    )}
                    
                    <div className={styles.downloadSection}>
                      <button
                        className={styles.downloadButton}
                        onClick={async () => {
                          try {
                            const fullUrl = buildFileUrl(selectedProof.fileUrl);
                            const response = await fetch(fullUrl, { credentials: 'include' });
                            if (!response.ok) {
                              throw new Error(`HTTP ${response.status}`);
                            }
                            const blob = await response.blob();
                            const objectUrl = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.href = objectUrl;
                            link.download = selectedProof.fileName || 'comprobante';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                            URL.revokeObjectURL(objectUrl);
                          } catch (error) {
                            console.error('Error downloading file:', error);
                            addNotification('No se pudo descargar el archivo', 'error');
                          }
                        }}
                      >
                        💾 Descargar archivo
                      </button>
                    </div>
                  </>
                )}
                
                {!selectedProof.fileUrl && (
                  <div className={styles.noFile}>
                    <p>No se pudo cargar la vista previa del archivo</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
